import { object, string, array, enum as zodEnum, ZodIssueCode, RefinementCtx } from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { areIntervalsOverlapping } from '@occc-app/lib/date';
import { IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { Weekday, KioskAdScheduleInput } from '@occc/graphql';

import { MAX_ASSET_NAME_LENGTH, MAX_ASSET_DESCRIPTION_LENGTH } from '../touchpoint.constants';

const OVERLAPPING_SCHEDULES_ERROR = 'This Ad Set has overlapping time blocks assigned';

type ScheduleValue = {
  days: Weekday[];
  startTime: string;
  endTime: string;
};

const REFERENCE_DATE = '2000-01-01';

/** Converts a time string (HH:MM:SS) to a Date object using a reference date. */
const timeStringToDate = (time: string): Date => new Date(`${REFERENCE_DATE}T${time}`);

/**
 * Determines if two time ranges overlap using date-fns areIntervalsOverlapping.
 *
 * Boundary behavior: Adjacent ranges that touch at boundaries are ALLOWED
 * (not considered overlapping). Back-to-back schedules like 09:00-12:00
 * followed by 12:00-15:00 are valid.
 */
const timeRangesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
  return areIntervalsOverlapping(
    { start: timeStringToDate(start1), end: timeStringToDate(end1) },
    { start: timeStringToDate(start2), end: timeStringToDate(end2) },
  );
};

/**
 * Checks if two schedules conflict by sharing at least one day AND having overlapping times.
 * Returns false for incomplete schedules (missing time values) to avoid false positives.
 */
const schedulesOverlap = (schedule1: ScheduleValue, schedule2: ScheduleValue): boolean => {
  const hasCommonDays = schedule1.days.some((day) => schedule2.days.includes(day));
  if (!hasCommonDays) return false;

  const hasValidTimes =
    schedule1.startTime && schedule1.endTime && schedule2.startTime && schedule2.endTime;
  if (!hasValidTimes) return false;

  return timeRangesOverlap(
    schedule1.startTime,
    schedule1.endTime,
    schedule2.startTime,
    schedule2.endTime,
  );
};

/**
 * Zod superRefine validator that checks all schedule pairs for conflicts.
 * Uses O(n²) comparison which is acceptable for typical schedule counts (<10).
 */
const validateNoOverlappingSchedules = (
  data: { schedules: ScheduleValue[] },
  ctx: RefinementCtx,
) => {
  const { schedules } = data;
  if (!schedules || schedules.length < 2) return;

  for (let i = 0; i < schedules.length; i++) {
    for (let j = i + 1; j < schedules.length; j++) {
      if (schedulesOverlap(schedules[i], schedules[j])) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: OVERLAPPING_SCHEDULES_ERROR,
          path: ['schedules', i, 'endTime'],
        });
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: OVERLAPPING_SCHEDULES_ERROR,
          path: ['schedules', j, 'endTime'],
        });
      }
    }
  }
};

const scheduleSchema = object({
  days: array(
    zodEnum([
      Weekday.Monday,
      Weekday.Tuesday,
      Weekday.Wednesday,
      Weekday.Thursday,
      Weekday.Friday,
      Weekday.Saturday,
      Weekday.Sunday,
    ]),
  ),
  startTime: string(),
  endTime: string(),
})
  .refine(
    (schedule) => {
      if (schedule.days.length > 0) {
        return schedule.startTime.trim().length > 0 && schedule.endTime.trim().length > 0;
      }
      return true;
    },
    { message: 'Start time and end time are required when days are selected', path: ['endTime'] },
  )
  .refine(
    (schedule) => {
      if (!schedule.startTime || !schedule.endTime) {
        return true;
      }
      return schedule.startTime < schedule.endTime;
    },
    { message: 'Start time must be before end time', path: ['endTime'] },
  )
  .refine(
    (schedule) => {
      const hasStartTime = schedule.startTime.trim().length > 0;
      const hasEndTime = schedule.endTime.trim().length > 0;
      if (hasStartTime && hasEndTime) {
        return schedule.days.length > 0;
      }
      return true;
    },
    { message: 'At least one weekday must be selected', path: ['days'] },
  );

const schema = object({
  name: string({ required_error: IS_REQUIRED })
    .trim()
    .min(1, IS_REQUIRED)
    .max(MAX_ASSET_NAME_LENGTH, `Name must be less than ${MAX_ASSET_NAME_LENGTH} characters`),
  description: string({ required_error: IS_REQUIRED })
    .trim()
    .min(1, IS_REQUIRED)
    .max(
      MAX_ASSET_DESCRIPTION_LENGTH,
      `Description must be less than ${MAX_ASSET_DESCRIPTION_LENGTH} characters`,
    ),
  assetId: string({ required_error: IS_REQUIRED })
    .min(1, IS_REQUIRED)
    .nullable()
    .refine((val) => val !== null && val !== undefined && val.trim().length > 0, {
      message: IS_REQUIRED,
    }),
  kioskIds: array(string()),
  schedules: array(scheduleSchema),
}).superRefine(validateNoOverlappingSchedules);

export const validate = makeValidate(schema);

const REQUIRED_FIELDS = ['name', 'description', 'assetId'];

export const hasRequiredFieldErrors = (errors: object | undefined): boolean =>
  !!errors && Object.keys(errors).some((key) => REQUIRED_FIELDS.includes(key));

export const isScheduleValid = (schedule: KioskAdScheduleInput): boolean =>
  schedule.days.length > 0 && !!schedule.startTime.trim() && !!schedule.endTime.trim();

export const mapScheduleToScheduleInput = ({
  days,
  startTime,
  endTime,
}: KioskAdScheduleInput): KioskAdScheduleInput => ({ days, startTime, endTime });
